/**
 * Popover hiển thị danh sách cột với checkbox + drag handle.
 * Dùng cùng `useFuturesColumns` — truyền các trường từ hook vào props.
 */
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import {
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  IconButton,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'

import type { DatasheetColumnLike, UseDatasheetColumnsMenuResult } from '../../utils/datasheetColumns/types'

interface Props<TCol extends DatasheetColumnLike & { label: string }> {
  controller: UseDatasheetColumnsMenuResult<TCol>
}

export function FuturesColumnsMenu<TCol extends DatasheetColumnLike & { label: string }>({
  controller,
}: Props<TCol>) {
  const {
    menuItems,
    hiddenSet,
    menuAnchor,
    closeMenu,
    toggleColumn,
    resetToDefault,
    dragKey,
    dragOverKey,
    onDragStart,
    onDragOver,
    onDrop,
    onDragEnd,
  } = controller

  return (
    <Popover
      open={menuAnchor != null}
      anchorEl={menuAnchor}
      onClose={closeMenu}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      PaperProps={{ sx: { p: 1.5, maxWidth: 340, maxHeight: 420, overflow: 'auto' } }}
    >
      <Button
        size="small"
        variant="outlined"
        fullWidth
        onClick={resetToDefault}
        sx={{ mb: 1, minHeight: 28, textTransform: 'none', fontSize: '0.72rem' }}
      >
        Default order
      </Button>
      <FormGroup>
        {menuItems.map((col) => {
          const checked = !hiddenSet.has(col.key)
          const isDragging = dragKey === col.key
          const isDragTarget = dragKey !== null && dragOverKey === col.key && dragKey !== col.key
          return (
            <Stack
              key={col.key}
              direction="row"
              alignItems="center"
              spacing={0.25}
              sx={{
                my: -0.25,
                opacity: isDragging ? 0.4 : 1,
                borderTop: isDragTarget ? '2px solid' : '2px solid transparent',
                borderColor: isDragTarget ? 'primary.main' : 'transparent',
                transition: 'border-color 80ms',
              }}
              onDragOver={onDragOver(col.key)}
              onDrop={onDrop(col.key)}
            >
              <Tooltip title="Drag to reorder">
                <IconButton
                  size="small"
                  disableRipple
                  draggable
                  onDragStart={onDragStart(col.key)}
                  onDragEnd={onDragEnd}
                  sx={{
                    p: 0.25,
                    cursor: 'grab',
                    '&:active': { cursor: 'grabbing' },
                  }}
                >
                  <DragIndicatorIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
              <FormControlLabel
                sx={{ alignItems: 'center', flex: 1, minWidth: 0, mr: 0 }}
                control={
                  <Checkbox
                    size="small"
                    checked={checked}
                    onChange={() => toggleColumn(col.key)}
                  />
                }
                label={<Typography variant="caption">{col.label}</Typography>}
              />
            </Stack>
          )
        })}
      </FormGroup>
    </Popover>
  )
}
